import React, { useState, useEffect, useRef } from 'react';
import {
  View,
  Text,
  ScrollView,
  Platform,
  Switch,
  TouchableOpacity,
  Modal,
  FlatList,
  TextInput as RNTextInput,
} from 'react-native';
import ConfirmDiscardModal from '../../../../components/ConfirmDiscardModal';
import MaterialIcons from 'react-native-vector-icons/MaterialIcons';
import { useTheme } from '../../../../theme/useTheme';
import { useDispatch, useSelector } from 'react-redux';
import LHOverlay from '../../../../components/LHOverlay';
import LhTextInput from '../../../../components/LhTextInput';
import LHDropDown from '../../../../components/LHDropDown';
import LhButton from '../../../../components/LhButton';
import FontFamily from '../../../../constants/fonts';
import { AppDispatch, RootState } from '../../../../store';
import {
  createRackingInspectionQuestion,
  updateRackingInspectionQuestion,
  patchRackingStatus,
} from '../../../../store/slices/adminSlice';
import {
  LOCATION_TYPE_OPTIONS,
  ORGANIZATION_TYPE_OPTIONS,
  THREE_PL_OPTIONS,
  SPARC_CATEGORY_OPTIONS,
  RACKING_SECTION_OPTIONS,
} from './kfaDropdownOptions';
import { getValidationState, REQUIRED_FIELDS_MESSAGE } from './validationUtils';
import { styles, msStyles } from './RackingInspectionAddOverlay.styles';

// ─── Self-contained MultiSelectDropdown ──────────────────────────────────────
// Avoids LHDropDown's array-wrapping bug for multi-select use cases.

interface MSOption {
  id: string | number;
  displayText: string;
}

interface MultiSelectDropdownProps {
  label?: string;
  options: MSOption[];
  selected: MSOption[];
  onChange: (selected: MSOption[]) => void;
  placeholder?: string;
  singleSelect?: boolean;
}

const MultiSelectDropdown: React.FC<MultiSelectDropdownProps> = ({
  label,
  options,
  selected,
  onChange,
  placeholder = 'Select...',
  singleSelect = false,
}) => {
  const { colors, resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark' && Platform.OS !== 'web';
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [menuStyle, setMenuStyle] = useState({ top: 0, left: 0, width: 200 });
  const triggerRef = useRef<any>(null);

  const toggle = () => {
    if (!open && triggerRef.current) {
      if (Platform.OS === 'web') {
        try {
          const rect = (triggerRef.current as any).getBoundingClientRect();
          const scrollY = typeof window !== 'undefined' ? window.scrollY || 0 : 0;
          setMenuStyle({ top: rect.bottom + scrollY, left: rect.left, width: rect.width });
        } catch (_) { /* ignore */ }
      } else {
        triggerRef.current.measureInWindow?.((x: number, y: number, w: number, h: number) => {
          setMenuStyle({ top: y + h, left: x, width: w });
        });
      }
    }
    setOpen((o) => !o);
  };

  const isSelected = (item: MSOption) =>
    selected.some((s) => String(s.id) === String(item.id));

  const handlePress = (item: MSOption) => {
    if (singleSelect) {
      onChange([item]);
      setOpen(false);
      setSearch('');
      return;
    }
    if (isSelected(item)) {
      onChange(selected.filter((s) => String(s.id) !== String(item.id)));
    } else {
      onChange([...selected, item]);
    }
  };

  const filtered = options.filter((o) =>
    o.displayText.toLowerCase().includes(search.toLowerCase()),
  );

  const displayText =
    selected.length === 0
      ? placeholder
      : selected.map((s) => s.displayText).join(', ');

  return (
    <View style={msStyles.container}>
      {label ? <Text style={[msStyles.label, isDark && { color: colors.text.secondary }]}>{label}</Text> : null}

      <TouchableOpacity
        ref={triggerRef}
        style={[msStyles.trigger, isDark && { backgroundColor: colors.input.background, borderColor: colors.input.border }]}
        onPress={toggle}
        activeOpacity={0.8}
      >
        <Text
          style={[msStyles.triggerText, selected.length === 0 && msStyles.placeholder, isDark && { color: selected.length === 0 ? colors.text.disabled : colors.text.primary }]}
          numberOfLines={1}
        >
          {displayText}
        </Text>
        <MaterialIcons name={open ? 'arrow-drop-up' : 'arrow-drop-down'} size={20} color={isDark ? colors.text.secondary : '#495057'} />
      </TouchableOpacity>

      <Modal visible={open} transparent animationType="none" onRequestClose={() => setOpen(false)}>
        <TouchableOpacity style={msStyles.backdrop} activeOpacity={1} onPress={() => setOpen(false)} />
        <View
          style={[
            msStyles.menu,
            Platform.OS === 'web'
              ? ({ position: 'fixed', top: menuStyle.top, left: menuStyle.left, width: menuStyle.width } as any)
              : { position: 'absolute', top: menuStyle.top, left: menuStyle.left, width: menuStyle.width },
            isDark && { backgroundColor: colors.background.elevated, borderColor: colors.border.default },
          ]}
        >
          <View style={[msStyles.searchBox, isDark && { backgroundColor: colors.input.background, borderColor: colors.border.divider }]}>
            <MaterialIcons name="search" size={16} color={isDark ? colors.text.secondary : '#888'} style={{ marginRight: 6 }} />
            <RNTextInput
              value={search}
              onChangeText={setSearch}
              placeholder="Search..."
              placeholderTextColor={isDark ? colors.text.disabled : '#AAAAAA'}
              style={[msStyles.searchInput, isDark && { color: colors.text.primary }]}
              onStartShouldSetResponder={() => true}
            />
          </View>
          <FlatList
            data={filtered}
            keyExtractor={(item) => String(item.id)}
            style={{ maxHeight: 185 }}
            keyboardShouldPersistTaps="always"
            renderItem={({ item }) => (
              <TouchableOpacity
                style={[msStyles.option, isSelected(item) && msStyles.optionSelected, isDark && { borderBottomColor: colors.border.divider }, isDark && isSelected(item) && { backgroundColor: '#1A2A3A' }]}
                onPress={() => handlePress(item)}
              >
                <Text style={[msStyles.optionText, isDark && { color: colors.text.primary }]}>{item.displayText}</Text>
                {isSelected(item) && <MaterialIcons name="check" size={18} color={isDark ? colors.brand.primary : '#0066CC'} />}
              </TouchableOpacity>
            )}
          />
        </View>
      </Modal>
    </View>
  );
};


// ─── Types ────────────────────────────────────────────────────────────────────

/** Full dataset for edit mode — mirrors the API response fields */
export interface RackingInspectionEditItem {
  id: string;                     // rowKey from API
  /** Maps to `qtext` in the POST/PUT payload */
  qtext: string;
  /** Maps to `shortForm` in the POST/PUT payload */
  shortForm?: string;
  section?: string;
  locationTypes?: string[];
  organizationType?: string;
  /** "YES" | "NO" | undefined */
  threePlStatus?: string;
  category?: string;
  status?: boolean;
  /** The sequential question number from the API — passed back unchanged on edit */
  qnum?: number;
  /** ISO timestamp from the API — used to enforce the 24-hour edit lock on text fields */
  createdAt?: string;
}

interface RackingInspectionAddOverlayProps {
  visible: boolean;
  onDismiss: () => void;
  /** 'create' opens a blank form; 'edit' pre-fills from selectedItem */
  mode?: 'create' | 'edit';
  selectedItem?: RackingInspectionEditItem;
  /** Called after a successful save so the parent can re-fetch the table */
  onSaveSuccess?: () => void;
}

interface RackingValidationErrors {
  qtext: boolean;
  shortForm: boolean;
  locationTypes: boolean;
  organizationType: boolean;
  threeplStatus: boolean;
  category: boolean;
  section: boolean;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const findOption = <T extends { id: string | number; displayText: string }>(
  options: T[],
  value: string | number | undefined | null,
): T | null => {
  if (value === undefined || value === null || value === '') return null;
  return options.find((o) => String(o.id).toLowerCase() === String(value).toLowerCase()) ?? null;
};

/** Find all matching options from a comma-separated string. */
const findOptions = <T extends { id: string | number; displayText: string }>(
  options: T[],
  value: string | undefined | null,
): T[] => {
  if (!value) return [];
  const parts = String(value).split(',').map((s) => s.trim()).filter(Boolean);
  return options.filter((o) =>
    parts.some((v) => String(o.id).toLowerCase() === v.toLowerCase()),
  );
};

// ─── Component ────────────────────────────────────────────────────────────────

const RackingInspectionAddOverlay: React.FC<RackingInspectionAddOverlayProps> = ({
  visible,
  onDismiss,
  mode = 'create',
  selectedItem,
  onSaveSuccess,
}) => {
  const { colors, resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark' && Platform.OS !== 'web';
  const dispatch = useDispatch<AppDispatch>();

  const isSaving = useSelector(
    (state: RootState) => state.configuration.rackingInspection.isLoading,
  );
  const saveError = useSelector(
    (state: RootState) => state.configuration.rackingInspection.error,
  );
  const rackingTotalCount = useSelector(
    (state: RootState) => state.configuration.rackingInspection.rackingStats?.totalElements ?? 0,
  );

  // ─── Form state ──────────────────────────────────────────────────────────────
  const [qtext, setQtext] = useState('');                           // → qtext
  const [shortForm, setShortForm] = useState('');                   // → shortForm
  const [section, setSection] = useState<any>(null);               // → section
  const [locationTypes, setLocationTypes] = useState<MSOption[]>([]); // → locationTypes[]
  const [organizationType, setOrganizationType] = useState<MSOption[]>([]); // → organizationType
  const [threeplStatus, setThreeplStatus] = useState<any>(null);   // → threePlStatus "YES"|"NO"
  const [category, setCategory] = useState<MSOption[]>([]);             // → category
  const [statusEnabled, setStatusEnabled] = useState<boolean>(true); // → status (edit only)
  // 24-hour text-field lock
  const isTextLocked = mode === 'edit' && !!selectedItem?.createdAt
    && (Date.now() - new Date(selectedItem.createdAt).getTime()) > 24 * 60 * 60 * 1000;

  const [validationMessage, setValidationMessage] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<RackingValidationErrors>({
    qtext: false,
    shortForm: false,
    locationTypes: false,
    organizationType: false,
    threeplStatus: false,
    category: false,
    section: false,
  });

  const isDirtyRef = useRef(false);
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);

  const clearFieldError = (field: keyof RackingValidationErrors) => {
    isDirtyRef.current = true;
    setFieldErrors((prev) => ({ ...prev, [field]: false }));
  };

  const validateForm = () => {
    const { fieldErrors: nextErrors, hasErrors } = getValidationState<keyof RackingValidationErrors>({
      qtext: !qtext.trim(),
      shortForm: !shortForm.trim(),
      locationTypes: locationTypes.length === 0,
      organizationType: organizationType.length === 0,
      threeplStatus: !threeplStatus,
      category: category.length === 0,
      section: !section,
    });
    setFieldErrors(nextErrors);
    if (hasErrors) {
      setValidationMessage(REQUIRED_FIELDS_MESSAGE);
      return false;
    }
    return true;
  };

  // ─── Scroll-lock (web only) ────────────────────────────────────────────────
  useEffect(() => {
    if (Platform.OS !== 'web') return;
    if (visible) {
      const scrollY = (window as any).scrollY || 0;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      (document.body.dataset as any).rackingOverlayScrollY = String(scrollY);
    } else {
      const savedY = parseInt((document.body.dataset as any).rackingOverlayScrollY || '0', 10);
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      window.scrollTo(0, savedY);
    }
    return () => {
      if (Platform.OS === 'web') {
        const savedY = parseInt((document.body.dataset as any).rackingOverlayScrollY || '0', 10);
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        window.scrollTo(0, savedY);
      }
    };
  }, [visible]);

  // ─── Reset / Pre-fill on visibility change ───────────────────────────────────
  useEffect(() => {
    if (!visible) {
      setQtext('');
      setShortForm('');
      setSection(null);
      setLocationTypes([]);
      setOrganizationType([]);
      setThreeplStatus(null);
      setCategory([]);
      setStatusEnabled(true);
      setValidationMessage(null);
      setFieldErrors({
        qtext: false,
        shortForm: false,
        locationTypes: false,
        organizationType: false,
        threeplStatus: false,
        category: false,
        section: false,
      });
      isDirtyRef.current = false;
      return;
    }

    if (mode === 'edit' && selectedItem) {
      setQtext(selectedItem.qtext ?? '');
      setShortForm(selectedItem.shortForm ?? '');
      setSection(findOption(RACKING_SECTION_OPTIONS, selectedItem.section));
      // locationTypes: restore from string[] to MSOption[]
      setLocationTypes(
        (selectedItem.locationTypes ?? []).map((lt) => ({ id: lt, displayText: lt })),
      );
      setOrganizationType(findOptions(ORGANIZATION_TYPE_OPTIONS, selectedItem.organizationType));
      // threePlStatus from API: "YES" | "NO" → map to THREE_PL_OPTIONS id "Yes" | "No"
      setThreeplStatus(
        selectedItem.threePlStatus !== undefined
          ? findOption(THREE_PL_OPTIONS, selectedItem.threePlStatus === 'YES' ? 'Yes' : 'No')
          : null,
      );
      setCategory(findOptions(SPARC_CATEGORY_OPTIONS, selectedItem.category));
      setStatusEnabled(selectedItem.status !== false);
    } else {
      setQtext('');
      setShortForm('');
      setSection(null);
      setLocationTypes([]);
      setOrganizationType([]);
      setThreeplStatus(null);
      setCategory([]);
      setStatusEnabled(true);
      setValidationMessage(null);
      setFieldErrors({
        qtext: false,
        shortForm: false,
        locationTypes: false,
        organizationType: false,
        threeplStatus: false,
        category: false,
        section: false,
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, mode, selectedItem]);

  // ─── Payload builder ─────────────────────────────────────────────────────────
  const buildPayload = () => {
    // locationTypes — extract id (which equals display name) from each selected option
    const locationTypesPayload: string[] = locationTypes.map((lt) => String(lt.id));

    // threePlStatus — "YES" | "NO" | "" per API spec
    const threePlStatus =
      threeplStatus?.id === 'Yes' ? 'YES' : threeplStatus?.id === 'No' ? 'NO' : '';

    const base = {
      qtext: qtext.trim(),
      shortForm: shortForm.trim(),
      section: String(section?.id ?? ''),
      locationTypes: locationTypesPayload,
      organizationType: organizationType.map((x) => String(x.id)).join(','),
      threePlStatus,
      category: category.map((x) => String(x.id)).join(','),
    };

    // Add mode: qnum = totalCount + 1; Edit mode: reuse the record's existing qnum
    if (mode === 'create') {
      return { ...base, qnum: rackingTotalCount + 1 };
    }
    return { ...base, ...(selectedItem?.qnum != null ? { qnum: selectedItem.qnum } : {}) };
  };

  // ─── Save handler ─────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    setValidationMessage(null);
    const payload = buildPayload();
    console.log('[RACKING] payload →', JSON.stringify(payload));

    try {
      if (mode === 'edit' && selectedItem?.id) {
        await dispatch(updateRackingInspectionQuestion({ id: selectedItem.id, ...payload })).unwrap();
        await dispatch(patchRackingStatus({ id: selectedItem.id, status: statusEnabled })).unwrap();
      } else {
        await dispatch(createRackingInspectionQuestion(payload)).unwrap();
      }
      onSaveSuccess?.();
      onDismiss();
    } catch (_err) {
      // Error surfaced via Redux saveError
    }
  };

  // ─── Render ───────────────────────────────────────────────────────────────────

  const handleCancel = () => {
    if (isDirtyRef.current) {
      setShowDiscardConfirm(true);
    } else {
      onDismiss();
    }
  };

  return (
    <>
      <ConfirmDiscardModal
        visible={showDiscardConfirm}
        onKeepEditing={() => setShowDiscardConfirm(false)}
        onDiscard={() => { setShowDiscardConfirm(false); onDismiss(); }}
      />
      <LHOverlay
        visible={visible}
        onDismiss={handleCancel}
        title={mode === 'edit' ? 'Edit Racking Inspection' : 'Add Racking Inspection'}
        size="medium"
        position="center"
        close={true}
      >
      <View style={[styles.overlayWrapper, isDark && { backgroundColor: colors.background.surface }]}>
        {validationMessage ? (
          <View style={styles.saveErrorBox}>
            <MaterialIcons
              name="error-outline"
              size={16}
              color="#C62828"
              style={{ marginRight: 8 }}
            />
            <Text style={styles.saveErrorText}>{validationMessage}</Text>
          </View>
        ) : null}

        <ScrollView style={styles.scrollContent} contentContainerStyle={styles.overlayContent}>

          {/* Status Toggle — edit mode only */}
          {mode === 'edit' && (
            <View style={styles.statusRow}>
              <Text style={[styles.statusLabel, isDark && { color: colors.text.primary }]}>Status:</Text>
              <Switch
                value={statusEnabled}
                onValueChange={(val) => {
                  isDirtyRef.current = true;
                  setStatusEnabled(val);
                }}
                thumbColor="#FFFFFF"
                trackColor={{ false: isDark ? '#555' : '#CCCCCC', true: '#2E7D32' }}
              />
              <Text style={[styles.statusValue, { color: statusEnabled ? '#2E7D32' : (isDark ? colors.text.secondary : '#757575') }]}>
                {statusEnabled ? 'Enabled' : 'Disabled'}
              </Text>
            </View>
          )}

          {/* ── Assessment Text ──────────────────────────────────────────────── */}
          <View style={[styles.assessmentSection, isDark && { backgroundColor: colors.background.elevated, borderColor: colors.border.divider }]}>
            <View style={styles.sectionTitleContainer}>
              <Text style={[styles.sectionTitle, isDark && { color: colors.text.primary }]}>Assessment Text</Text>
            </View>

            {/* Key Focus Area → `qtext` */}
            <View style={styles.inputGroup}>
              <View style={styles.labelRow}>
                <Text style={[styles.inputLabel, isDark && { color: colors.text.primary }]}>
                  Key Focus Area{' '}
                  <Text style={[styles.greyText, isDark && { color: colors.text.secondary }]}>(negative observation statement)</Text>
                </Text>
                <Text style={styles.requiredLabel}>*Required</Text>
              </View>
              <LhTextInput
                value={qtext}
                onChangeText={(val) => {
                  setQtext(val);
                  clearFieldError('qtext');
                }}
                placeholder="e.g., Rack upright is damaged"
                multiline
                numberOfLines={3}
                editable={!isTextLocked}
                style={styles.textInput}
                wrapperStyle={[styles.inputWrapper, fieldErrors.qtext && styles.inputError, isTextLocked && styles.inputLocked]}
              />
              <View style={styles.charCountRow}>
                {isTextLocked && <Text style={styles.lockedHelperText}>Locked 24 hours after creation.</Text>}
                <Text style={[styles.charCount, isDark && { color: colors.text.secondary }]}>{qtext.length}/500</Text>
              </View>
            </View>

            {/* Short Form → `shortForm` */}
            <View style={styles.inputGroup}>
              <View style={styles.labelRow}>
                <Text style={[styles.inputLabel, isDark && { color: colors.text.primary }]}>
                  Short Form <Text style={[styles.greyText, isDark && { color: colors.text.secondary }]}>(brief question / label)</Text>
                </Text>
                <Text style={styles.requiredLabel}>*Required</Text>
              </View>
              <LhTextInput
                value={shortForm}
                onChangeText={(val) => {
                  setShortForm(val);
                  clearFieldError('shortForm');
                }}
                placeholder="e.g., Uprights plumb?"
                multiline
                numberOfLines={3}
                editable={!isTextLocked}
                style={styles.textInput}
                wrapperStyle={[styles.inputWrapper, fieldErrors.shortForm && styles.inputError, isTextLocked && styles.inputLocked]}
              />
              <View style={styles.charCountRow}>
                {isTextLocked && <Text style={styles.lockedHelperText}>Locked 24 hours after creation.</Text>}
                <Text style={[styles.charCount, isDark && { color: colors.text.secondary }]}>{shortForm.length}/500</Text>
              </View>
            </View>

            <View style={[styles.infoBox, isDark && { backgroundColor: '#0D2A4A', borderColor: '#1A4070' }]}>
              <MaterialIcons name="info" size={20} color={isDark ? colors.brand.primary : '#034895'} style={styles.infoIcon} />
              <Text style={[styles.infoText, isDark && { color: colors.text.primary }]}>
                The Key Focus Area is the primary text. Short Form is used as a concise label
                or question in inspection views.
              </Text>
            </View>
          </View>

          {/* ── Shared Attributes ─────────────────────────────────────────────── */}
          <View style={[styles.sharedAttributesSection, isDark && { backgroundColor: colors.background.elevated, borderColor: colors.border.divider }]}>
            <View style={styles.sectionTitleContainer}>
              <Text style={[styles.sectionTitle, isDark && { color: colors.text.primary }]}>Shared Attributes</Text>
            </View>

            {/* Row 1: Location Types (multi) & Organization Type */}
            <View style={[styles.dropdownRow, { zIndex: 30 }]}>
              <View style={[styles.dropdownColumn, { zIndex: 30 }, fieldErrors.locationTypes && styles.dropdownErrorWrapper]}>
                <View style={styles.labelRow}>
                  <Text style={[styles.fieldLabel, isDark && { color: colors.text.secondary }]}>Location Types</Text>
                  <Text style={styles.requiredLabel}>*Required</Text>
                </View>
                <MultiSelectDropdown
                  label=""
                  options={LOCATION_TYPE_OPTIONS}
                  selected={locationTypes}
                  onChange={(val) => {
                    setLocationTypes(val);
                    clearFieldError('locationTypes');
                  }}
                  placeholder="Select Location Type(s)"
                />
              </View>
              <View style={[styles.dropdownColumn, { zIndex: 30 }, fieldErrors.organizationType && styles.dropdownErrorWrapper]}>
                <View style={styles.labelRow}>
                  <Text style={[styles.fieldLabel, isDark && { color: colors.text.secondary }]}>Organization Type</Text>
                  <Text style={styles.requiredLabel}>*Required</Text>
                </View>
                <MultiSelectDropdown
                  label=""
                  options={ORGANIZATION_TYPE_OPTIONS}
                  selected={organizationType}
                  onChange={(val) => { setOrganizationType(val); clearFieldError('organizationType'); }}
                  placeholder="Select Organization Type"
                />
              </View>
            </View>

            {/* Row 2: 3PL Status & Category */}
            <View style={[styles.dropdownRow, { zIndex: 20 }]}>
              <View style={[styles.dropdownColumn, { zIndex: 20 }, fieldErrors.threeplStatus && styles.dropdownErrorWrapper]}>
                <View style={styles.labelRow}>
                  <Text style={[styles.fieldLabel, isDark && { color: colors.text.secondary }]}>3PL Status</Text>
                  <Text style={styles.requiredLabel}>*Required</Text>
                </View>
                <LHDropDown
                  label=""
                  childList={THREE_PL_OPTIONS}
                  selectedValue={threeplStatus}
                  setSelectedValue={(val: any) => {
                    setThreeplStatus(val);
                    clearFieldError('threeplStatus');
                  }}
                  placeholder="Select 3PL Status"
                />
              </View>
              <View style={[styles.dropdownColumn, { zIndex: 20 }, fieldErrors.category && styles.dropdownErrorWrapper]}>
                <View style={styles.labelRow}>
                  <Text style={[styles.fieldLabel, isDark && { color: colors.text.secondary }]}>Category</Text>
                  <Text style={styles.requiredLabel}>*Required</Text>
                </View>
                <MultiSelectDropdown
                  label=""
                  options={SPARC_CATEGORY_OPTIONS}
                  selected={category}
                  onChange={(val) => { setCategory(val); clearFieldError('category'); }}
                  placeholder="Select Category"
                  singleSelect
                />
              </View>
            </View>
          </View>

          {/* ── Racking Inspection Attributes ─────────────────────────────────── */}
          <View style={[styles.rackingAttributesSection, isDark && { backgroundColor: colors.background.elevated, borderColor: colors.border.divider }]}>
            <Text style={[styles.attributesSectionTitle, isDark && { color: colors.text.primary }]}>Racking Inspection Attributes</Text>

            {/* Section — full width */}
            <View style={[styles.fullWidthDropdown, fieldErrors.section && styles.dropdownErrorWrapper]}>
              <View style={styles.labelRow}>
                <Text style={[styles.fieldLabel, isDark && { color: colors.text.secondary }]}>Section</Text>
                <Text style={styles.requiredLabel}>*Required</Text>
              </View>
              <LHDropDown
                label=""
                childList={RACKING_SECTION_OPTIONS}
                selectedValue={section}
                setSelectedValue={(val: any) => {
                  setSection(val);
                  clearFieldError('section');
                }}
                placeholder="Select Section"
              />
            </View>
          </View>

        </ScrollView>

        {/* ── Footer ────────────────────────────────────────────────────────────── */}
        <View style={[styles.overlayFooter, isDark && { borderTopColor: colors.border.default }]}>
          {saveError && visible ? (
            <View style={styles.saveErrorBox}>
              <MaterialIcons
                name="error-outline"
                size={16}
                color="#C62828"
                style={{ marginRight: 8 }}
              />
              <Text style={styles.saveErrorText}>{saveError}</Text>
            </View>
          ) : null}
          <View style={styles.footerButtons}>
            <LhButton text="Cancel" onPress={handleCancel} variant="secondary" disabled={isSaving} />
            <LhButton text="Save" onPress={handleSave} variant="primary" isLoading={isSaving} disabled={isSaving} />
          </View>
        </View>
      </View>
    </LHOverlay>
    </>
  );
};

export default RackingInspectionAddOverlay;

// ─── Styles ───────────────────────────────────────────────────────────────────

